import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { PlayCircle, Copy, Check, AlertCircle, Clock } from "lucide-react";
import { toast } from "sonner";

type HttpMethod = "GET" | "POST" | "PATCH" | "DELETE";
type ResourceType = "assets" | "asset-lists";

interface Endpoint {
  method: HttpMethod;
  path: string;
  description: string;
  requiresBody: boolean;
  requiresId: boolean;
  resource: ResourceType;
}

const endpoints: Endpoint[] = [
  // Asset endpoints
  { method: "GET", path: "/assets", description: "List all assets", requiresBody: false, requiresId: false, resource: "assets" },
  { method: "GET", path: "/assets?id=eq.{id}", description: "Get asset by ID", requiresBody: false, requiresId: true, resource: "assets" },
  { method: "POST", path: "/assets", description: "Create new asset", requiresBody: true, requiresId: false, resource: "assets" },
  { method: "PATCH", path: "/assets?id=eq.{id}", description: "Update asset", requiresBody: true, requiresId: true, resource: "assets" },
  { method: "DELETE", path: "/assets?id=eq.{id}", description: "Delete asset", requiresBody: false, requiresId: true, resource: "assets" },
  // Asset List endpoints
  { method: "GET", path: "/asset-lists", description: "List all asset lists", requiresBody: false, requiresId: false, resource: "asset-lists" },
  { method: "GET", path: "/asset-lists?id=eq.{id}", description: "Get asset list by ID", requiresBody: false, requiresId: true, resource: "asset-lists" },
  { method: "POST", path: "/asset-lists", description: "Create new asset list", requiresBody: true, requiresId: false, resource: "asset-lists" },
  { method: "PATCH", path: "/asset-lists?id=eq.{id}", description: "Update asset list", requiresBody: true, requiresId: true, resource: "asset-lists" },
  { method: "DELETE", path: "/asset-lists?id=eq.{id}", description: "Delete asset list", requiresBody: false, requiresId: true, resource: "asset-lists" },
];

const defaultRequestBodies: Record<ResourceType, { create: string; update: string }> = {
  "assets": {
    create: '{\n  "name": "My Asset",\n  "list_id": "your-list-id-here",\n  "comment": "Optional comment"\n}',
    update: '{\n  "name": "Updated Asset Name",\n  "comment": "Updated comment"\n}'
  },
  "asset-lists": {
    create: '{\n  "title": "My Asset List",\n  "description": "Optional description"\n}',
    update: '{\n  "title": "Updated List Title",\n  "description": "Updated description"\n}'
  }
};

interface ApiPlaygroundProps {
  prefillEndpoint?: string | null;
  onClearPrefill?: () => void;
}

const ApiPlayground = ({ prefillEndpoint, onClearPrefill }: ApiPlaygroundProps) => {
  const [selectedEndpoint, setSelectedEndpoint] = useState<Endpoint>(endpoints[0]);
  const [apiKey, setApiKey] = useState("");
  const [resourceId, setResourceId] = useState("");
  const [listIdFilter, setListIdFilter] = useState("");
  const [requestBody, setRequestBody] = useState(defaultRequestBodies["assets"].create);
  const [response, setResponse] = useState<{ status: number; body: string; headers: Record<string, string>; duration: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Handle prefill from docs "Try it" buttons
  useEffect(() => {
    if (prefillEndpoint !== null && prefillEndpoint !== undefined) {
      const index = parseInt(prefillEndpoint);
      if (!isNaN(index) && index >= 0 && index < endpoints.length) {
        const endpoint = endpoints[index];
        setSelectedEndpoint(endpoint);
        if (endpoint.requiresBody) {
          const bodyType = endpoint.method === "POST" ? "create" : "update";
          setRequestBody(defaultRequestBodies[endpoint.resource][bodyType]);
        }
      }
      onClearPrefill?.();
    }
  }, [prefillEndpoint, onClearPrefill]);

  const getMethodColor = (method: HttpMethod) => {
    switch (method) {
      case "GET": return "bg-blue-500";
      case "POST": return "bg-green-500";
      case "PATCH": return "bg-yellow-500";
      case "DELETE": return "bg-red-500";
      default: return "bg-gray-500";
    }
  };

  const getResourceColor = (resource: ResourceType) => {
    return resource === "assets" ? "bg-purple-500/20 text-purple-700 dark:text-purple-300" : "bg-teal-500/20 text-teal-700 dark:text-teal-300";
  };

  const handleEndpointChange = (value: string) => {
    const endpoint = endpoints[parseInt(value)];
    setSelectedEndpoint(endpoint);
    
    if (endpoint.requiresBody) {
      const bodyType = endpoint.method === "POST" ? "create" : "update";
      setRequestBody(defaultRequestBodies[endpoint.resource][bodyType]);
    }
  };

  const buildUrl = () => {
    const baseUrl = "https://api.testassets.io/v1";
    let path = selectedEndpoint.path;
    
    if (selectedEndpoint.requiresId && resourceId) {
      path = path.replace("{id}", resourceId);
    }
    
    if (selectedEndpoint.method === "GET" && !selectedEndpoint.requiresId && selectedEndpoint.resource === "assets" && listIdFilter) {
      path += `?list_id=eq.${listIdFilter}`;
    }
    
    return `${baseUrl}${path}`;
  };

  const executeRequest = async () => {
    if (!apiKey) {
      toast.error("API key is required");
      return;
    }

    if (selectedEndpoint.requiresId && !resourceId) {
      const idType = selectedEndpoint.resource === "assets" ? "Asset" : "Asset List";
      toast.error(`${idType} ID is required for this endpoint`);
      return;
    }

    setLoading(true);
    setResponse(null);

    const startTime = performance.now();

    try {
      const url = buildUrl();
      const headers: Record<string, string> = {
        'apikey': apiKey,
        'Content-Type': 'application/json',
      };

      const options: RequestInit = {
        method: selectedEndpoint.method,
        headers,
      };

      if (selectedEndpoint.requiresBody) {
        try {
          JSON.parse(requestBody);
          options.body = requestBody;
        } catch (e) {
          toast.error("Invalid JSON in request body");
          setLoading(false);
          return;
        }
      }

      const res = await fetch(url, options);
      const duration = Math.round(performance.now() - startTime);
      
      const responseHeaders: Record<string, string> = {};
      res.headers.forEach((value, key) => {
        responseHeaders[key] = value;
      });

      let body = "";
      try {
        const json = await res.json();
        body = JSON.stringify(json, null, 2);
      } catch (e) {
        body = await res.text();
      }

      setResponse({
        status: res.status,
        body,
        headers: responseHeaders,
        duration,
      });

      if (res.ok) {
        toast.success(`Request successful (${res.status}) — ${duration}ms`);
      } else {
        toast.error(`Request failed (${res.status}) — ${duration}ms`);
      }
    } catch (error) {
      const duration = Math.round(performance.now() - startTime);
      toast.error("Request failed: " + (error instanceof Error ? error.message : "Unknown error"));
      setResponse({
        status: 0,
        body: JSON.stringify({ error: error instanceof Error ? error.message : "Unknown error" }, null, 2),
        headers: {},
        duration,
      });
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const generateCurlCommand = () => {
    const url = buildUrl();
    let curl = `curl -X ${selectedEndpoint.method} '${url}' \\\n  -H 'apikey: ${apiKey || 'YOUR_API_KEY'}' \\\n  -H 'Content-Type: application/json'`;
    
    if (selectedEndpoint.requiresBody) {
      curl += ` \\\n  -d '${requestBody}'`;
    }
    
    return curl;
  };

  const getIdLabel = () => {
    return selectedEndpoint.resource === "assets" ? "Asset ID" : "Asset List ID";
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return "text-green-600 dark:text-green-400";
    if (status >= 400 && status < 500) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  const getDurationColor = (ms: number) => {
    if (ms < 300) return "text-green-600 dark:text-green-400";
    if (ms < 1000) return "text-yellow-600 dark:text-yellow-400";
    return "text-red-600 dark:text-red-400";
  };

  return (
    <div className="space-y-6 p-6">
      <Card>
        <CardHeader>
          <CardTitle>API Playground</CardTitle>
          <CardDescription>
            Test API endpoints directly in your browser. Select an endpoint, enter your API key, and execute requests.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Endpoint Selection */}
          <div className="space-y-2">
            <Label>Endpoint</Label>
            <Select
              value={endpoints.indexOf(selectedEndpoint).toString()}
              onValueChange={handleEndpointChange}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {endpoints.map((endpoint, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    <div className="flex items-center gap-2">
                      <Badge className={`${getMethodColor(endpoint.method)} text-white`}>
                        {endpoint.method}
                      </Badge>
                      <Badge variant="outline" className={getResourceColor(endpoint.resource)}>
                        {endpoint.resource}
                      </Badge>
                      <span>{endpoint.path}</span>
                      <span className="text-muted-foreground text-sm">- {endpoint.description}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* API Key */}
          <div className="space-y-2">
            <Label htmlFor="apiKey">API Key *</Label>
            <Input
              id="apiKey"
              type="password"
              placeholder="sk_live_..."
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Your API key from Settings → API Keys
            </p>
          </div>

          {/* Resource ID (for specific endpoints) */}
          {selectedEndpoint.requiresId && (
            <div className="space-y-2">
              <Label htmlFor="resourceId">{getIdLabel()} *</Label>
              <Input
                id="resourceId"
                placeholder={`UUID of the ${selectedEndpoint.resource === "assets" ? "asset" : "asset list"}`}
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
              />
            </div>
          )}

          {/* List ID Filter (for GET all assets) */}
          {selectedEndpoint.method === "GET" && !selectedEndpoint.requiresId && selectedEndpoint.resource === "assets" && (
            <div className="space-y-2">
              <Label htmlFor="listIdFilter">Filter by List ID (Optional)</Label>
              <Input
                id="listIdFilter"
                placeholder="UUID of the asset list"
                value={listIdFilter}
                onChange={(e) => setListIdFilter(e.target.value)}
              />
            </div>
          )}

          {/* Request Body */}
          {selectedEndpoint.requiresBody && (
            <div className="space-y-2">
              <Label htmlFor="requestBody">Request Body</Label>
              <Textarea
                id="requestBody"
                value={requestBody}
                onChange={(e) => setRequestBody(e.target.value)}
                className="font-mono text-sm min-h-[150px]"
              />
            </div>
          )}

          {/* Execute Button */}
          <Button
            onClick={executeRequest}
            disabled={loading}
            className="w-full"
            size="lg"
          >
            <PlayCircle className="mr-2 h-5 w-5" />
            {loading ? "Executing..." : "Execute Request"}
          </Button>

          {/* cURL Command */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Equivalent cURL Command</Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyToClipboard(generateCurlCommand())}
              >
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
            <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm">
              <code>{generateCurlCommand()}</code>
            </pre>
          </div>
        </CardContent>
      </Card>

      {/* Response */}
      {response && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3">
              <span>Response</span>
              <Badge variant={response.status >= 200 && response.status < 300 ? "default" : "destructive"}>
                {response.status}
              </Badge>
              <div className="flex items-center gap-1.5 ml-auto">
                <Clock className={`h-4 w-4 ${getDurationColor(response.duration)}`} />
                <span className={`text-sm font-mono font-medium ${getDurationColor(response.duration)}`}>
                  {response.duration}ms
                </span>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="body">
              <TabsList>
                <TabsTrigger value="body">Body</TabsTrigger>
                <TabsTrigger value="headers">Headers</TabsTrigger>
              </TabsList>
              <TabsContent value="body" className="mt-4">
                <div className="relative">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2 z-10"
                    onClick={() => copyToClipboard(response.body)}
                  >
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  </Button>
                  <pre className="bg-muted p-4 rounded-lg overflow-x-auto text-sm max-h-[400px]">
                    <code>{response.body}</code>
                  </pre>
                </div>
              </TabsContent>
              <TabsContent value="headers" className="mt-4">
                <div className="bg-muted p-4 rounded-lg overflow-x-auto">
                  <pre className="text-sm">
                    {Object.entries(response.headers).map(([key, value]) => (
                      <div key={key}>
                        <span className="font-semibold">{key}:</span> {value}
                      </div>
                    ))}
                  </pre>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}

      {/* Help Alert */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          <strong>Tip:</strong> You can use the playground to test your API integration before implementing it in your application.
          All requests are executed client-side in your browser. Use the <strong>Assets</strong> endpoints to manage individual assets,
          and <strong>Asset Lists</strong> endpoints to manage collections.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default ApiPlayground;
